/**
 * Shortcut a la página de sesiones del evento "Inicio de sprint". Lista
 * todos los eventos recurrentes globales y deja al usuario elegir cuál
 * revisar el histórico. Hoy es opinada (kind=meeting); en el futuro
 * podríamos generalizarla a /events.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CalendarRange, ArrowUpRight } from "lucide-react";
import type { CalendarEventRow } from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";

export const Route = createFileRoute("/sesiones")({
  component: SesionesPage,
});

function SesionesPage() {
  // keepMasters=1 → el backend NO oculta los masters anclados aunque
  // tengan instancias sincronizadas en Google. Esta vista lista
  // específicamente esos masters (kind=meeting + recurrence) — sin la
  // flag el dedup del calendario los oculta.
  const { data, isLoading } = useQuery<{ events: CalendarEventRow[] }>({
    queryKey: ["calendar-events", "all-recurring"],
    queryFn: () => api.get("/calendar-events?scope=all&keepMasters=1"),
  });

  const recurring = (data?.events ?? []).filter((e) => e.recurrence && e.kind === "meeting");

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Juntas"
        title="Sesiones de juntas recurrentes"
        subtitle="Inicios de sprint, demos, retros. Cada ocurrencia mantiene su propio registro: descripción, link Mural, adjuntos y grabación."
      />

      {isLoading ? (
        <div className="grid place-items-center py-12">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      ) : recurring.length === 0 ? (
        <div className="card-elevated py-12 text-center">
          <CalendarRange className="size-10 mx-auto mb-3 text-base-content/30" />
          <p className="text-sm text-base-content/55">
            Aún no hay juntas recurrentes registradas.{" "}
            <Link to="/admin/calendar-events" className="text-primary hover:underline">
              Crea una en admin
            </Link>{" "}
            con tipo "Junta" y una recurrencia para que aparezca aquí.
          </p>
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {recurring.map((e) => (
            <li key={e.id}>
              <Link
                to="/events/$eventId/sessions"
                params={{ eventId: e.id }}
                className="card-elevated p-4 block hover:bg-base-200/40 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-display font-semibold truncate">{e.name}</h3>
                    <p className="text-xs text-base-content/55 mt-1 capitalize">
                      {e.recurrence === "daily" && "Diaria"}
                      {e.recurrence === "weekly" && "Semanal"}
                      {e.recurrence === "biweekly" && "Cada 2 semanas"}
                      {e.recurrence === "monthly" && "Mensual"}
                      {e.recurrence === "yearly" && "Anual"}
                      {e.startTime ? ` · ${e.startTime}` : ""}
                    </p>
                  </div>
                  <ArrowUpRight className="size-4 text-base-content/40 shrink-0" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
